import { jsPDF } from "jspdf";
import { structureLabel } from "@/lib/utils";

/**
 * Formatteur EUR compatible jsPDF (Latin-1). N'utilise QUE des caractères ASCII :
 * - espace ASCII pour séparateur de milliers (au lieu du narrow-no-break-space U+202F
 *   d'Intl.NumberFormat qui rend un "/" parasite dans les polices standard de jsPDF)
 * - virgule pour décimales, "€" pour la devise (supporté par Helvetica/WinAnsi)
 */
const EUR = (n) => {
  const v = Number(n || 0);
  const [intPart, decPart] = Math.abs(v).toFixed(2).split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const sign = v < 0 ? "-" : "";
  return `${sign}${grouped},${decPart} \u20AC`;
};

/**
 * Capture un <canvas> WebGL (rendu 3D react-three-fiber) en dataURL PNG.
 * Le canvas doit avoir été créé avec `preserveDrawingBuffer: true` pour que
 * toDataURL() retourne bien le dernier contenu rendu (sinon buffer vidé par le navigateur).
 * Retourne { dataUrl, width, height } — ou null si indisponible/échec.
 */
function canvasToPngDataUrl(canvasEl) {
  if (!canvasEl) return null;
  try {
    return { dataUrl: canvasEl.toDataURL("image/png"), width: canvasEl.width, height: canvasEl.height };
  } catch (e) {
    console.warn("Capture 3D (canvas) → PNG échouée", e);
    return null;
  }
}

async function svgMarkupToPngDataUrl(markup, targetWidth = 2400) {
  try {
    const size = markup.match(/viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/);
    const sourceW = Number(size?.[1] || 1600);
    const sourceH = Number(size?.[2] || 900);
    const targetHeight = Math.round(targetWidth * (sourceH / sourceW));
    const img = new Image();
    const encoded = window.btoa(unescape(encodeURIComponent(markup)));
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = `data:image/svg+xml;base64,${encoded}`;
    });
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");
    context.fillStyle = "#FDFBF7";
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(img, 0, 0, targetWidth, targetHeight);
    return { dataUrl: canvas.toDataURL("image/png"), width: targetWidth, height: targetHeight };
  } catch (error) {
    console.warn("SVG assemblage → PNG failed", error);
    return null;
  }
}

const xml = (value) => String(value ?? "").replace(/[<>&'\"]/g, (char) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" }[char]));

/** Charge une image raster (croquis client) et retourne ses dimensions naturelles. */
async function rasterInfo(dataUrl) {
  if (!dataUrl) return null;
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.8), width: canvas.width, height: canvas.height });
      } catch (error) {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

/**
 * Produit une liste exhaustive d'éléments pour un meuble entier, agrégée depuis ses sections.
 * Ex: ["2 portes battantes (doubles)", "3 tablettes fixes", "1 tablette mobile", "2 tiroirs Merivo", "1 penderie"]
 */
function exhaustiveElementList(breakdownSections, config) {
  const lines = [];
  let doorsSimpleLeft = 0, doorsSimpleRight = 0, doorsDouble = 0;
  let shelvesFixed = 0, shelvesMobile = 0;
  let drawersNormal = 0, drawersTraversant = 0;
  let penderies = 0;
  let mirrors = 0;

  (breakdownSections || []).forEach((sb, i) => {
    // Doors
    if (sb.has_door) {
      const ds = sb.door_specs;
      if (ds && ds.leaf_count >= 2) doorsDouble++;
      else if (sb.door_standard === "single_left") doorsSimpleLeft++;
      else doorsSimpleRight++;
    }
    // Elements from config sections
    const sectionCfg = (config.sections || [])[i] || {};
    const elements = sectionCfg.elements || [];
    elements.forEach((el) => {
      if (el.kind === "shelf_fixed") shelvesFixed++;
      else if (el.kind === "shelf_mobile") shelvesMobile++;
      else if (el.kind === "drawer") {
        if ((el.span_sections || 1) > 1) drawersTraversant++;
        else drawersNormal++;
      }
      else if (el.kind === "penderie") penderies++;
      else if (el.kind === "mirror") mirrors++;
    });
    // Fallback from breakdown counts if no elements array
    if (!elements.length) {
      shelvesFixed += sb.shelves_fixed || 0;
      shelvesMobile += sb.shelves_mobile || 0;
      if (sb.drawers) drawersNormal += sb.drawers;
      if (sb.has_penderie_bar) penderies++;
      mirrors += sb.mirrors || 0;
    }
  });

  if (doorsDouble) lines.push(`${doorsDouble} porte${doorsDouble > 1 ? "s" : ""} double${doorsDouble > 1 ? "s" : ""} (2 vantaux)`);
  if (doorsSimpleLeft) lines.push(`${doorsSimpleLeft} porte${doorsSimpleLeft > 1 ? "s" : ""} battante${doorsSimpleLeft > 1 ? "s" : ""} (charniere gauche)`);
  if (doorsSimpleRight) lines.push(`${doorsSimpleRight} porte${doorsSimpleRight > 1 ? "s" : ""} battante${doorsSimpleRight > 1 ? "s" : ""} (charniere droite)`);
  if (shelvesFixed) lines.push(`${shelvesFixed} tablette${shelvesFixed > 1 ? "s" : ""} fixe${shelvesFixed > 1 ? "s" : ""}`);
  if (shelvesMobile) lines.push(`${shelvesMobile} tablette${shelvesMobile > 1 ? "s" : ""} mobile${shelvesMobile > 1 ? "s" : ""}`);
  if (drawersNormal) lines.push(`${drawersNormal} tiroir${drawersNormal > 1 ? "s" : ""}`);
  if (drawersTraversant) lines.push(`${drawersTraversant} tiroir${drawersTraversant > 1 ? "s" : ""} traversant${drawersTraversant > 1 ? "s" : ""}`);
  if (penderies) lines.push(`${penderies} penderie${penderies > 1 ? "s" : ""}`);
  if (mirrors) lines.push(`${mirrors} miroir${mirrors > 1 ? "s" : ""}`);

  // Options from config
  if (config.options?.has_back_panel) lines.push("Dos melamine 16 mm");
  if (config.options?.feet_visible !== false) lines.push("Pieds");
  if (config.options?.plinth) lines.push("Plinthe integree");
  if (config.options?.fileur_left_cm > 0) lines.push(`Fileur gauche ${Math.round(config.options.fileur_left_cm * 10)} mm`);
  if (config.options?.fileur_right_cm > 0) lines.push(`Fileur droit ${Math.round(config.options.fileur_right_cm * 10)} mm`);
  if (config.options?.fileur_top_cm > 0) lines.push(`Fileur haut ${Math.round(config.options.fileur_top_cm * 10)} mm`);

  return lines;
}

function sectionTechnicalDescription(section, breakdown, config, index) {
  const sectionConfig = (config.sections || [])[index] || {};
  const widthCm = sectionConfig.width_cm || config.total_width / Math.max(1, (config.sections || []).length);
  const parts = [`Largeur structure : ${Math.round(widthCm * 10)} mm`];
  const door = breakdown.door_specs;
  if (section.has_door && door) parts.push(`${door.leaf_count} vantail${door.leaf_count > 1 ? "x" : ""} ${door.leaf_width_mm} × ${door.leaf_height_mm} mm · ${door.opening} · ${door.position || "toute hauteur"} · jeu ${door.perimeter_clearance_mm} mm${door.center_gap_mm ? ` · joint central ${door.center_gap_mm} mm` : ""}`);
  const elements = sectionConfig.elements || [];
  const shelves = elements.filter((element) => element.kind === "shelf_fixed" || element.kind === "shelf_mobile");
  if (shelves.length) parts.push(`Tablettes : ${shelves.slice().sort((a, b) => a.y_mm - b.y_mm).map((shelf) => `${shelf.kind === "shelf_fixed" ? "fixe" : "mobile"} à ${Math.round(shelf.y_mm)} mm`).join(", ")}`);
  else if (section.shelves_fixed || section.shelves_mobile) parts.push(`Tablettes : ${section.shelves_fixed || 0} fixe(s), ${section.shelves_mobile || 0} mobile(s)`);
  const drawers = elements.filter((element) => element.kind === "drawer");
  if (drawers.length) parts.push(`Tiroirs : ${drawers.map((drawer) => `axe ${Math.round(drawer.y_mm)} mm${drawer.span_sections > 1 ? ` · traversant ${drawer.span_sections} sections` : ""}`).join(", ")}`);
  if (section.has_penderie_bar) parts.push("Barre penderie incluse");
  parts.push(config.options?.has_back_panel ? "Dos mélaminé 16 mm" : "Sans dos (selon configuration)");
  return parts;
}

function assemblyVisualSvg(quote, mode, catalog) {
  const assembly = quote.assembly;
  const moduleB = (quote.items || []).find((item) => item.id === assembly?.module_b_item_id);
  if (!assembly || !moduleB) return null;
  const moduleA = { config: quote.config, label: assembly.module_a_label || "Module A" };
  const moduleBObj = { config: moduleB.config, label: assembly.module_b_label || "Module B" };
  // Ordre G/D configurable via module_order
  const swapped = assembly.module_order === "b_left";
  const modules = swapped ? [moduleBObj, moduleA] : [moduleA, moduleBObj];
  const combinedWidth = modules.reduce((sum, module) => sum + module.config.total_width * 10, 0);
  const highest = Math.max(...modules.map((module) => module.config.height * 10));
  const scale = Math.min(0.74, 1250 / combinedWidth, 430 / highest);
  const drawWidth = combinedWidth * scale;
  const startX = (1600 - drawWidth) / 2;
  const baseY = 690;
  let cursor = startX;
  const technical = mode === "technical";
  const labels = [];
  const body = modules.map((module, index) => {
    const width = module.config.total_width * 10 * scale;
    const height = module.config.height * 10 * scale;
    const top = baseY - height;
    const sections = module.config.sections || [];
    const pxPerMm = scale;
    const totalSectionCm = sections.reduce((s, sec) => s + (sec.width_cm || 0), 0) || module.config.total_width;
    const sectionWidths = sections.map((sec) => ((sec.width_cm || totalSectionCm / sections.length) / totalSectionCm) * width);
    let sectionCursor = 0;
    const carcassFinish = catalog?.finishes?.find((f) => f.id === module.config.carcass_finish_id);
    const doorsFinish = catalog?.finishes?.find((f) => f.id === (module.config.doors_finish_id || module.config.carcass_finish_id));
    const fill = technical ? "#FDFBF7" : (carcassFinish?.swatch || (index === 0 ? "#C8A16B" : "#B8935A"));
    const doorFill = doorsFinish?.swatch || "#D8C09A";
    const detail = sections.map((section, sectionIndex) => {
      const secW = sectionWidths[sectionIndex];
      const left = cursor + sectionCursor;
      sectionCursor += secW;
      const divider = sectionIndex > 0 ? `<line x1="${left}" y1="${top + 4}" x2="${left}" y2="${baseY - 4}" stroke="#374151" stroke-width="3"/>` : "";
      const door = (!technical && section.has_door) ? `<rect x="${left + 7}" y="${top + 9}" width="${secW - 14}" height="${height - 18}" rx="4" fill="${doorFill}" stroke="#374151" stroke-width="2" opacity="0.85"/>` : "";
      const moduleRight = cursor + width;
      const els = technical ? (section.elements || []).map((el) => {
        const elCenterY = baseY - el.y_mm * pxPerMm;
        const spanW = ((el.span_sections || 1) > 1 ? sectionWidths.slice(sectionIndex, sectionIndex + Math.min(el.span_sections, sections.length - sectionIndex)).reduce((a, b) => a + b, 0) : secW);
        const elX = left + 5;
        const elW = Math.min(spanW - 10, moduleRight - elX - 5);
        if (elW <= 0) return "";
        if (el.kind === "shelf_fixed" || el.kind === "shelf_mobile") {
          const dash = el.kind === "shelf_mobile" ? ' stroke-dasharray="6 3"' : "";
          return `<line x1="${elX}" y1="${elCenterY}" x2="${elX + elW}" y2="${elCenterY}" stroke="#374151" stroke-width="2.5"${dash}/>`;
        }
        if (el.kind === "drawer") {
          const dH = Math.max(10, (el.height_mm || 150) * pxPerMm);
          const dTop = Math.max(top + 4, elCenterY - dH / 2);
          const dBottom = Math.min(baseY - 4, dTop + dH);
          const clampedH = dBottom - dTop;
          if (clampedH < 6) return "";
          return `<rect x="${elX}" y="${dTop}" width="${elW}" height="${clampedH}" rx="3" fill="rgba(0,0,0,0.06)" stroke="#374151" stroke-width="2"/><line x1="${elX + elW * 0.3}" y1="${dTop + clampedH / 2}" x2="${elX + elW * 0.7}" y2="${dTop + clampedH / 2}" stroke="#374151" stroke-width="2.5"/>`;
        }
        if (el.kind === "penderie") {
          return `<line x1="${elX + 4}" y1="${elCenterY}" x2="${elX + elW - 4}" y2="${elCenterY}" stroke="#374151" stroke-width="3" stroke-linecap="round"/><circle cx="${elX + elW / 2}" cy="${elCenterY + 5}" r="3" fill="none" stroke="#374151" stroke-width="1.5"/>`;
        }
        return "";
      }).join("") : "";
      return `${divider}${els}${door}`;
    }).join("");
    const narrow = width < 260;
    const cx = cursor + width / 2;
    if (narrow) {
      const shortLabel = module.label.replace(/^Module\s*/i, "");
      labels.push(
        `<text x="${cx}" y="${baseY + 50}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="22" font-weight="700" fill="#172033">${xml(shortLabel)}</text>` +
        `<text x="${cx}" y="${baseY + 70}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="12" fill="#52606F">${Math.round(module.config.total_width * 10)} mm</text>` +
        `<text x="${cx}" y="${baseY + 84}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="11" fill="#52606F">H${Math.round(module.config.height * 10)} \u00d7 P${Math.round(module.config.depth * 10)}</text>`
      );
    } else {
      labels.push(
        `<text x="${cx}" y="${baseY + 48}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="28" font-weight="700" fill="#172033">${xml(module.label)}</text>` +
        `<text x="${cx}" y="${baseY + 76}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="22" fill="#52606F">${Math.round(module.config.total_width * 10)} \u00d7 ${Math.round(module.config.height * 10)} \u00d7 ${Math.round(module.config.depth * 10)} mm</text>`
      );
    }
    const dimension = technical ? `<line x1="${cursor}" y1="${top - 14}" x2="${cursor + width}" y2="${top - 14}" stroke="#0465C1" stroke-width="2"/><path d="M${cursor},${top - 14} l12,-7 v14z M${cursor + width},${top - 14} l-12,-7 v14z" fill="#0465C1"/><text x="${cursor + width / 2}" y="${top - 25}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="21" font-weight="700" fill="#0465C1">${Math.round(module.config.total_width * 10)} mm</text>` : "";
    const content = `<g><rect x="${cursor}" y="${top}" width="${width}" height="${height}" rx="3" fill="${fill}" stroke="#172033" stroke-width="4"/>${detail}${dimension}</g>`;
    cursor += width;
    return content;
  }).join("");
  const topBand = "";
  const plinth = assembly.continuous_plinth_mm ? `<rect x="${startX}" y="${baseY}" width="${drawWidth}" height="26" fill="#7C6043"/><text x="${startX + drawWidth / 2}" y="${baseY + 116}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="21" fill="#52606F">PLINTHE CONTINUE \u00b7 ${assembly.continuous_plinth_mm} mm</text>` : `<line x1="${startX}" y1="${baseY}" x2="${startX + drawWidth}" y2="${baseY}" stroke="#172033" stroke-width="5"/>`;
  const joinX = startX + modules[0].config.total_width * 10 * scale;
  const centralJoin = `<line x1="${joinX}" y1="${baseY - highest * scale}" x2="${joinX}" y2="${baseY + 26}" stroke="#0465C1" stroke-width="3" stroke-dasharray="10 7"/><text x="${joinX}" y="${baseY - highest * scale - 16}" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="20" font-weight="700" fill="#0465C1">JONCTION CHANTIER</text>`;
  const title = technical ? "VUE TECHNIQUE ATELIER \u2014 MODULES UNITAIRES" : "VUE D\u2019ENSEMBLE FINIE \u2014 ENSEMBLE ASSEMBL\u00c9";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 860"><rect width="1600" height="860" fill="#FDFBF7"/><text x="800" y="64" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="31" font-weight="700" fill="#172033">${title}</text><text x="800" y="98" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="20" fill="#52606F">Ensemble assembl\u00e9 sur chantier (2 caissons jumel\u00e9s)</text>${topBand}${body}${plinth}${centralJoin}${labels.join("")}</svg>`;
}



/**
 * Génère un PDF de devis en marque blanche (nom + email dynamiques de l'artisan).
 * @param {object} quote
 * @param {object} [opts]
 * @param {HTMLCanvasElement|null} [opts.canvasElement]      — rendu 3D du meuble principal
 * @param {HTMLCanvasElement[]|null} [opts.itemCanvasElements] — rendus 3D des meubles additionnels
 * @param {object}          [opts.catalog] — catalogue ({finishes, handles, sdb_handles}), pour que les visuels d'ensemble assemblé reprennent la vraie couleur de finition
 * @param {string}          [opts.convertUrl]
 * @param {object}          [opts.user]   — { company, email, contact_name }
 */
export async function generateQuotePDF(quote, opts = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const MARGIN = 15;
  const INNER_W = W - MARGIN * 2;

  // ==== MARQUE BLANCHE : identité de l'artisan connecté ====
  const user = opts.user || {};
  const brandName =
    (user.company && user.company.trim()) ||
    (quote.user_company && quote.user_company.trim()) ||
    "Votre Devis Professionnel";
  const brandEmail =
    (user.email && user.email.trim()) ||
    (quote.user_email && quote.user_email.trim()) ||
    "";
  const brandContact =
    (user.contact_name && user.contact_name.trim()) || "";

  // ============================================================
  // BANDEAU HEADER (identité artisan)
  // ============================================================
  doc.setFillColor(4, 101, 193);
  doc.rect(0, 0, W, 32, "F");
  doc.setTextColor(253, 251, 247);

  let textStartX = MARGIN;

  // Injection du logo de l'artisan
  if (user.logo) {
    try {
      doc.addImage(user.logo, textStartX, 4, 24, 24);
      textStartX += 30; // on décale le texte vers la droite
    } catch (e) {
      console.warn("Erreur lors de l'ajout du logo au PDF:", e);
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(brandName.substring(0, 60), textStartX, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (brandContact) {
    doc.text(brandContact, textStartX, 20);
  }
  if (brandEmail) {
    doc.text(brandEmail, textStartX, brandContact ? 25 : 20);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("DEVIS", W - MARGIN, 14, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Réf. ${quote.reference}`, W - MARGIN, 20, { align: "right" });
  doc.text(
    new Date(quote.created_at).toLocaleDateString("fr-FR"),
    W - MARGIN,
    25,
    { align: "right" }
  );

  let y = 40;

  // ============================================================
  // BLOC : Projet + Client (colonne gauche) / Récap dimensions (colonne droite)
  // ============================================================
  const colW = (INNER_W - 6) / 2;
  const col1X = MARGIN;
  const col2X = MARGIN + colW + 6;

  doc.setTextColor(45, 42, 38);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("PROJET & CLIENT", col1X, y);
  doc.text("CONFIGURATION", col2X, y);
  doc.setDrawColor(4, 101, 193);
  doc.setLineWidth(0.6);
  doc.line(col1X, y + 1.5, col1X + 40, y + 1.5);
  doc.line(col2X, y + 1.5, col2X + 40, y + 1.5);

  // ---- Colonne 1 : Projet + Client
  let y1 = y + 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(45, 42, 38);
  if (quote.project_name) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(quote.project_name, col1X, y1);
    y1 += 6;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const cli = quote.client || {};
  if (cli.company) { doc.text(cli.company, col1X, y1); y1 += 4.5; }
  if (cli.contact_name) { doc.text(cli.contact_name, col1X, y1); y1 += 4.5; }
  if (cli.email) {
    doc.setTextColor(92, 88, 82);
    doc.text(cli.email, col1X, y1); y1 += 4.5;
    doc.setTextColor(45, 42, 38);
  }
  if (cli.phone) {
    doc.setTextColor(92, 88, 82);
    doc.text(cli.phone, col1X, y1); y1 += 4.5;
    doc.setTextColor(45, 42, 38);
  }

  // ---- Colonne 2 : Configuration essentielle — TOUS les meubles du devis
  let y2 = y + 8;
  const c = quote.config;
  const b = quote.breakdown;
  const assembly = quote.assembly;
  const mainHasDoor = (c.sections || []).some((s) => s.has_door);
  const allItems = quote.items || [];

  // Lister chaque meuble additionnel d'abord (Meuble 1, 2...) puis le principal (dernier)
  const allMeublesForPage1 = [
    ...allItems.map((it, i) => ({
      num: i + 1,
      config: it.config,
      finishName: it.carcass_finish_name || quote.carcass_finish_name || "-",
      templateName: it.template_name || quote.template_name,
      hasDoor: (it.config.sections || []).some((s) => s.has_door),
    })),
    {
      num: allItems.length + 1,
      config: c,
      finishName: quote.carcass_finish_name || "-",
      templateName: quote.template_name,
      hasDoor: mainHasDoor,
    },
  ];

  doc.setFontSize(9);
  allMeublesForPage1.forEach((m, mIdx) => {
    if (mIdx > 0) y2 += 2;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(4, 101, 193);
    doc.text(allMeublesForPage1.length > 1 ? `MEUBLE ${m.num}` : "MEUBLE PRINCIPAL", col2X, y2);
    doc.setDrawColor(4, 101, 193);
    doc.setLineWidth(0.4);
    doc.line(col2X, y2 + 1.2, col2X + 30, y2 + 1.2);
    y2 += 5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 115, 108);
    const dims = `${m.config.total_width * 10} × ${m.config.height * 10} × ${m.config.depth * 10} mm`;
    doc.text(`${structureLabel(m.templateName, m.hasDoor)} — ${dims}`, col2X, y2);
    y2 += 4;
    doc.setTextColor(45, 42, 38);
    doc.text(`Caisson : ${m.finishName} · ${m.config.sections.length} section(s) · Qté ${m.config.quantity || 1}`, col2X, y2);
    y2 += 4;
  });

  y = Math.max(y1, y2) + 8;  // Dégagement incompressible après en-tête

  if (assembly) {
    const finishedVisual = await svgMarkupToPngDataUrl(assemblyVisualSvg(quote, "finished", opts.catalog));
    const technicalVisual = await svgMarkupToPngDataUrl(assemblyVisualSvg(quote, "technical", opts.catalog));
    // Pas de saut de page forcé — flux continu, mais vérifier qu'il reste
    // assez de place pour le bandeau titre + au moins une image (≈55mm)
    if (y > H - 80) { doc.addPage(); y = 20; }
    doc.setFillColor(4, 101, 193);
    doc.rect(MARGIN, y, INNER_W, 8, "F");
    doc.setTextColor(253, 251, 247);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("ENSEMBLE ASSEMBLE SUR CHANTIER", W / 2, y + 5.5, { align: "center" });
    y += 10;
    // Capture 3D assemblage si disponible
    const assembly3dImg = opts.assembly3dCanvas ? canvasToPngDataUrl(opts.assembly3dCanvas) : null;
    const drawAssemblyVisual = (title, image) => {
      // Vérifier qu'il reste assez de place pour le titre + image + marge
      const estH = image ? Math.min(35, 90 * (image.height / image.width)) + 8 : 30;
      if (y + estH > H - 15) { doc.addPage(); y = 20; }
      doc.setTextColor(45, 42, 38);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(title, MARGIN, y);
      y += 5;  // Dégagement titre → cadre image (5mm min)
      const maxVisW = 90;
      const maxVisH = 35;
      const height = image ? Math.min(maxVisH, maxVisW * (image.height / image.width)) : 20;
      const width = image ? height / (image.height / image.width) : maxVisW;
      const x = MARGIN + (INNER_W - width) / 2;
      doc.setDrawColor(220, 215, 205);
      doc.rect(MARGIN, y, INNER_W, height + 3);
      if (image) doc.addImage(image.dataUrl, "PNG", x, y + 2, width, height, undefined, "FAST");
      else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(140, 135, 128);
        doc.text("(Visuel indisponible)", W / 2, y + height / 2, { align: "center" });
      }
      y += height + 8;  // dégagement après chaque visuel assemblage
    };
    drawAssemblyVisual("ASSEMBLAGE 3D", assembly3dImg);
    drawAssemblyVisual("VUE D’ENSEMBLE FINIE", finishedVisual);
    drawAssemblyVisual("VUE TECHNIQUE ATELIER — MODULE A / MODULE B", technicalVisual);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(45, 42, 38);
    const moduleB = (quote.items || []).find((item) => item.id === assembly.module_b_item_id);
    const technicalLines = [
      `Module A : ${c.total_width * 10} × ${c.height * 10} × ${c.depth * 10} mm`,
      moduleB ? `Module B : ${moduleB.config.total_width * 10} × ${moduleB.config.height * 10} × ${moduleB.config.depth * 10} mm` : "Module B : voir détail de fabrication",
      assembly.continuous_plinth_mm ? `1× Plinthe continue ${assembly.continuous_plinth_mm} mm` : null,
    ].filter(Boolean);
    technicalLines.forEach((line) => { doc.text(`• ${line}`, MARGIN + 2, y); y += 4.5; });
    y += 6;  // Dégagement après section assemblage
  }

  // ============================================================
  // VISUELS 3D + DÉTAIL PAR SECTION — un bloc complet par meuble du projet
  // (meuble principal + tous les meubles additionnels, chacun avec son propre visuel)
  // ============================================================
  const itemsForVisuals = quote.items || [];
  const hasMultiVisuals = itemsForVisuals.length > 0;
  // Les meubles additionnels ont été créés AVANT le meuble principal (= le dernier modifié,
  // donc numéroté en dernier) — on les affiche donc dans l'ordre chronologique de création
  // (Meuble 1, 2, 3...), le meuble principal (quote.config) toujours en dernier avec le
  // numéro le plus élevé. Le titre affiché est TOUJOURS "Meuble N", jamais le libellé interne
  // libre (ex: "mis en attente") qui n'a de sens que dans l'éditeur, pas sur un document client.
  const allMeubles = [
    ...itemsForVisuals.map((it, i) => ({
      title: `Meuble ${i + 1} — ${structureLabel(it.template_name, (it.config.sections || []).some((s) => s.has_door))}`,
      canvasEl: (opts.itemCanvasElements && opts.itemCanvasElements[i]) || document.querySelector(`#cabinet-3d-item-${i} canvas`),
      sections: it.breakdown.sections,
      config: it.config,
    })),
    {
      title: `Meuble ${itemsForVisuals.length + 1} — ${structureLabel(quote.template_name, mainHasDoor)}`,
      canvasEl: opts.canvasElement || document.querySelector("#cabinet-3d-main canvas"),
      sections: b.sections,
      config: c,
    },
  ];

  for (let mIdx = 0; mIdx < allMeubles.length; mIdx++) {
    const m = allMeubles[mIdx];
    const visualImg = canvasToPngDataUrl(m.canvasEl);
    const visualBoxH = 40;
    // Bloc complet = bandeau (5) + image (visualBoxH+2) + detail (~20) + marge (8)
    // Si on ne peut pas caser au moins le bandeau + image, nouvelle page
    const neededH = 5 + visualBoxH + 2 + 20;
    if (y + neededH > H - 10) { doc.addPage(); y = 20; }
    // Séparateur entre meubles
    if (mIdx > 0) {
      doc.setDrawColor(200, 195, 185);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, y, W - MARGIN, y);
      y += 4;
    }
    // Bandeau titre du visuel
    doc.setFillColor(245, 241, 232);
    doc.rect(MARGIN, y, INNER_W, 6, "F");
    doc.setTextColor(92, 88, 82);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(
      hasMultiVisuals ? `VISUEL 3D — ${m.title}`.toUpperCase().substring(0, 70) : "VISUEL 3D DE LA CONFIGURATION",
      MARGIN + 3, y + 4
    );
    y += 6;

    if (visualImg) {
      const ratio = visualImg.height / visualImg.width;
      let imgW = INNER_W - 30;
      let imgH = imgW * ratio;
      if (imgH > visualBoxH) {
        imgH = visualBoxH;
        imgW = imgH / ratio;
      }
      const imgX = MARGIN + (INNER_W - imgW) / 2;
      const containerH = imgH + 6;  // padding haut (2) + bas (4) pour contenir la perspective 3D
      doc.setDrawColor(220, 215, 205);
      doc.setLineWidth(0.2);
      doc.rect(MARGIN, y, INNER_W, containerH);
      doc.addImage(visualImg.dataUrl, "PNG", imgX, y + 2, imgW, imgH, undefined, "FAST");
      y += containerH + 6;  // dégagement après le cadre image
    } else {
      doc.setDrawColor(220, 215, 205);
      doc.rect(MARGIN, y, INNER_W, 24);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(160, 155, 148);
      doc.text("(Visuel 3D indisponible)", W / 2, y + 13, { align: "center" });
      y += 28;
    }

    // Détail de la configuration par section, propre à CE meuble
    if (y > H - 25) { doc.addPage(); y = 20; }
    doc.setTextColor(45, 42, 38);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Détail par section", MARGIN, y);
    y += 3;
    doc.setDrawColor(220, 215, 205);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 3;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    m.sections.forEach((s, i) => {
      if (y > H - 15) { doc.addPage(); y = 20; }
      const label = s.id || String.fromCharCode(65 + i);
      const parts = sectionTechnicalDescription(s, s, m.config, i);
      doc.setTextColor(120, 115, 108);
      doc.text(`Section ${label}`, MARGIN, y);
      doc.setTextColor(45, 42, 38);
      const detailLines = doc.splitTextToSize(parts.join(" · "), INNER_W - 30);
      doc.text(detailLines, MARGIN + 28, y);
      y += Math.max(4, detailLines.length * 3.5 + 0.5);
    });
    y += 6;  // Dégagement incompressible après chaque bloc meuble
  }

  // ============================================================
  // CROQUIS / RELEVÉ CLIENT — référence atelier
  // ============================================================
  const sketchEntries = [
    ...(quote.items || []).map((it, i) => ({ label: `Meuble ${i + 1}`, sketch: it.config?.options?.customer_structure_sketch })),
    { label: `Meuble ${(quote.items || []).length + 1}`, sketch: c.options?.customer_structure_sketch },
  ].filter((entry) => entry.sketch?.data_url);

  for (const entry of sketchEntries) {
    const raster = await rasterInfo(entry.sketch.data_url);
    if (!raster) continue;
    if (y > H - 60) { doc.addPage(); y = 20; }
    doc.setFillColor(245, 241, 232);
    doc.rect(MARGIN, y, INNER_W, 6, "F");
    doc.setTextColor(92, 88, 82);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(`CROQUIS / RELEVE CLIENT — ${entry.label}`.toUpperCase().substring(0, 78), MARGIN + 3, y + 4.2);
    y += 6;
    const ratio = raster.height / raster.width;
    let imgW = Math.min(INNER_W - 20, 120);
    let imgH = imgW * ratio;
    const maxH = 70;
    if (imgH > maxH) { imgH = maxH; imgW = imgH / ratio; }
    const imgX = MARGIN + (INNER_W - imgW) / 2;
    doc.setDrawColor(220, 215, 205);
    doc.setLineWidth(0.4);
    doc.rect(MARGIN, y, INNER_W, imgH + 3);
    doc.addImage(raster.dataUrl, "JPEG", imgX, y + 1.5, imgW, imgH, undefined, "SLOW");
    y += imgH + 4;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(140, 135, 128);
    doc.text(`Document fourni par le client (${entry.sketch.name}) — cotes a controler avant fabrication`, MARGIN, y);
    y += 4;
  }

  // ============================================================
  // TABLEAU FACTURATION (devis structuré : Désignation | Qté | Unité | PU HT | Total HT)
  // ============================================================
  y += 6;  // Dégagement avant le tableau de facturation
  const items = quote.items || [];
  const hasMulti = items.length > 0;

  // Colonnes (mm) — total = INNER_W (180)
  const colDesW = 108;                                   // Désignation : élargie pour éviter le débordement
  const colQtyW = 10;
  const colUnitW = 14;
  const colPuW = 22;
  const colTotW = INNER_W - colDesW - colQtyW - colUnitW - colPuW; // ~26mm
  const colDes = MARGIN;
  const colQty = colDes + colDesW;
  const colUnit = colQty + colQtyW;
  const colPu = colUnit + colUnitW;
  const colTot = colPu + colPuW;

  // Header du tableau
  if (y > H - 40) { doc.addPage(); y = 20; }
  doc.setFillColor(4, 101, 193);
  doc.rect(MARGIN, y, INNER_W, 7, "F");
  doc.setTextColor(253, 251, 247);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Designation", colDes + 2, y + 5);
  doc.text("Qte", colQty + colQtyW / 2, y + 5, { align: "center" });
  doc.text("Unite", colUnit + colUnitW / 2, y + 5, { align: "center" });
  doc.text("PU HT", colPu + colPuW - 1, y + 5, { align: "right" });
  doc.text("Total HT", colTot + colTotW - 1, y + 5, { align: "right" });
  y += 7;

  // Ligne utilitaire pour dessiner une ligne du tableau
  const drawRow = (desLines, qty, unit, pu, tot, opts2 = {}) => {
    // Wrapper chaque ligne de texte dans la largeur de la colonne Désignation
    const maxTextW = colDesW - 4; // 2mm padding de chaque côté
    const wrappedLines = [];
    const rawLines = Array.isArray(desLines) ? desLines : [String(desLines)];
    rawLines.forEach((l, i) => {
      doc.setFont("helvetica", i === 0 ? "bold" : "normal");
      doc.setFontSize(i === 0 ? 8 : 7.2);
      const split = doc.splitTextToSize(l, maxTextW);
      wrappedLines.push(...split.map((txt, j) => ({ txt, bold: i === 0 && j === 0, size: i === 0 ? 8 : 7.2 })));
    });
    const lineH = 3.2;
    const rowH = Math.max(5.5, wrappedLines.length * lineH + 2);
    if (y + rowH > H - 8) { doc.addPage(); y = 20; }
    // Fond alterné (bandes claires)
    if (opts2.zebra) {
      doc.setFillColor(248, 245, 238);
      doc.rect(MARGIN, y, INNER_W, rowH, "F");
    }
    doc.setDrawColor(230, 225, 215);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y + rowH, W - MARGIN, y + rowH);
    // Texte désignation — confiné dans la colonne
    wrappedLines.forEach((wl, i) => {
      doc.setFont("helvetica", wl.bold ? "bold" : "normal");
      doc.setFontSize(wl.size);
      doc.setTextColor(wl.bold ? 45 : 92, wl.bold ? 42 : 88, wl.bold ? 38 : 82);
      doc.text(wl.txt, colDes + 2, y + 3.8 + i * lineH);
    });
    // Colonnes de prix — largeurs fixes, alignement protégé
    doc.setTextColor(45, 42, 38);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    if (qty != null) doc.text(String(qty), colQty + colQtyW / 2, y + 4.2, { align: "center" });
    if (unit != null) doc.text(String(unit), colUnit + colUnitW / 2, y + 4.2, { align: "center" });
    if (pu != null) doc.text(EUR(pu), colPu + colPuW - 1, y + 4.2, { align: "right" });
    if (tot != null) {
      doc.setFont("helvetica", "bold");
      doc.text(EUR(tot), colTot + colTotW - 1, y + 4.2, { align: "right" });
    }
    y += rowH;
  };

  const drawBillingSection = (title) => {
    if (y > H - 20) { doc.addPage(); y = 20; }
    doc.setFillColor(245, 241, 232);
    doc.rect(MARGIN, y, INNER_W, 6, "F");
    doc.setTextColor(92, 88, 82);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(title, MARGIN + 2, y + 4.2);
    y += 6;
  };

  // Ligne principale : meuble configuré (supplements déjà inclus dans unit_price_ht -> les décrire dans la désignation)
  drawBillingSection("FOURNITURE — CAISSONS ET ÉLÉMENTS ASSOCIÉS");

  // Meubles additionnels d'abord (créés EN PREMIER chronologiquement → Meuble 1, 2...), le
  // meuble principal (canvas modifié EN DERNIER) toujours en dernier avec le numéro le plus
  // élevé — même ordre/numérotation que les visuels plus haut. Jamais `it.label` (texte libre
  // interne à l'éditeur, qui n'a pas sa place sur un document client).
  if (hasMulti) {
    items.forEach((it, i) => {
      const itHasDoor = (it.config.sections || []).some((s) => s.has_door);
      const label = `Meuble ${i + 1} — ${structureLabel(it.template_name, itHasDoor)} ${it.config.total_width * 10}×${it.config.height * 10}×${it.config.depth * 10} mm`;
      const spec = `Caisson : ${it.carcass_finish_name || "-"}${itHasDoor ? ` · Portes : ${it.doors_finish_name || "-"}` : ""}`;
      const elList = exhaustiveElementList(it.breakdown.sections, it.config);
      const elLine = elList.length ? `Elements : ${elList.join(" · ")}` : "";
      const descLines = [label, spec];
      if (elLine) descLines.push(elLine);
      drawRow(descLines, it.config.quantity || 1, "ens.", it.breakdown.unit_price_ht ?? it.breakdown.subtotal_ht, it.breakdown.subtotal_ht ?? it.breakdown.total_ht ?? it.breakdown.total_ttc, { zebra: i % 2 === 0 });
      // Lignes de majoration masquées sur le devis client (voir rapport admin exclusif)
    });
  }

  const mainTitle = `${hasMulti ? `Meuble ${items.length + 1} — ` : ""}${structureLabel(quote.template_name, mainHasDoor)} — ${c.total_width * 10}×${c.height * 10}×${c.depth * 10} mm — ${c.sections.length} section(s)`;
  const specLines = [];
  specLines.push(`Caisson : ${quote.carcass_finish_name || "-"}${mainHasDoor ? ` · Portes : ${quote.doors_finish_name || quote.carcass_finish_name || "-"}` : ""}`);
  const optDetails = [];
  if (b.feet_extra_price) optDetails.push(`${b.feet_total} pieds`);
  if (b.doors_finish_surcharge) optDetails.push("finition portes differente");
  if (b.carcass_finish_surcharge) optDetails.push("finition caisson premium");
  if (b.fileur_cost) optDetails.push("fileurs habillage");
  if (b.plinth_cost) optDetails.push("plinthe integree");
  if (b.coulissant_surcharge) optDetails.push("preparation coulissantes");
  if (b.no_back_adjust) optDetails.push("sans fond (remise)");
  if (optDetails.length) specLines.push(`Options : ${optDetails.join(" · ")}`);
  const mainElList = exhaustiveElementList(b.sections, c);
  if (mainElList.length) specLines.push(`Elements : ${mainElList.join(" · ")}`);
  drawRow([mainTitle, ...specLines], b.quantity, "ens.", b.unit_price_ht, b.subtotal_ht);
  // Lignes de majoration masquées sur le devis client (voir rapport admin exclusif)

  if (assembly) {
    const moduleB = (quote.items || []).find((item) => item.id === assembly.module_b_item_id);
    drawRow("Ensemble assemble sur chantier (2 caissons jumeles)", null, null, null, null, { zebra: true });
    if (assembly.continuous_plinth_mm) drawRow(`Plinthe continue ${assembly.continuous_plinth_mm} mm`, 1, "pce", null, null, { zebra: true });
    if (moduleB) drawRow(`Repérage atelier : Module A + Module B (${moduleB.template_name || "caisson"})`, null, null, null, null, { zebra: true });
  }
  drawRow("Sous-total fourniture", null, null, null, quote.furniture_total_ht || (quote.total_ht_all_items || 0) - (quote.installation_fee_ht || 0) - (quote.delivery_price_ht || 0), { bold: true });

  drawBillingSection("POSE ET FRAIS DE PORT");
  if (quote.installation_fee_applied && quote.installation_fee_ht) {
    drawRow("Forfait de pose — tarif configuré dans le back-office artisan", 1, "forf.", quote.installation_fee_ht, quote.installation_fee_ht, { zebra: true });
  } else {
    drawRow("Forfait de pose — non retenu", null, null, null, null, { zebra: true });
  }
  if (quote.delivery_applied && quote.delivery_price_ht) {
    const deliveryLabel = quote.delivery_tier === "50km" ? "Livraison — zone jusqu’à 50 km" : "Livraison — zone de 50 à 100 km";
    drawRow(`${deliveryLabel} (${quote.delivery_city || ""}, ${quote.delivery_distance_km} km)`, 1, "forf.", quote.delivery_price_ht, quote.delivery_price_ht, { zebra: true });
  } else {
    drawRow("Livraison — non retenue (tarification par zone 50 km / 100 km)", null, null, null, null, { zebra: true });
  }

  // ============================================================
  // BLOC TOTAUX (bas-droite)
  // ============================================================
  y += 6;  // Dégagement entre tableau et totaux
  if (y > H - 55) { doc.addPage(); y = 20; }
  const netHt = quote.total_ht_all_items || b.total_ht || 0;
  const tva = netHt * 0.20;
  const ttc = netHt + tva;
  const acompte = ttc * 0.30;

  // Zone totaux : moitié droite
  const totBoxX = MARGIN + INNER_W / 2;
  const totBoxW = INNER_W / 2;
  const totLabelX = totBoxX + 4;
  const totValueX = totBoxX + totBoxW - 4;

  // Net HT
  doc.setDrawColor(220, 215, 205);
  doc.setLineWidth(0.3);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(92, 88, 82);
  doc.text("Net HT", totLabelX, y + 5);
  doc.setTextColor(45, 42, 38);
  doc.setFont("helvetica", "bold");
  doc.text(EUR(netHt), totValueX, y + 5, { align: "right" });
  doc.line(totBoxX, y + 7, totBoxX + totBoxW, y + 7);
  y += 8;

  // TVA (20%)
  doc.setFont("helvetica", "normal");
  doc.setTextColor(92, 88, 82);
  doc.text("TVA (20%)", totLabelX, y + 5);
  doc.setTextColor(45, 42, 38);
  doc.setFont("helvetica", "bold");
  doc.text(EUR(tva), totValueX, y + 5, { align: "right" });
  doc.line(totBoxX, y + 7, totBoxX + totBoxW, y + 7);
  y += 8;

  // Total TTC (bandeau bleu foncé)
  doc.setFillColor(17, 17, 17);
  doc.rect(totBoxX, y, totBoxW, 10, "F");
  doc.setTextColor(253, 251, 247);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Total TTC", totLabelX, y + 6.5);
  doc.text(EUR(ttc), totValueX, y + 6.5, { align: "right" });
  y += 12;

  // Acompte 30% TTC (bandeau bleu clair)
  doc.setFillColor(4, 101, 193);
  doc.rect(totBoxX, y, totBoxW, 9, "F");
  doc.setTextColor(253, 251, 247);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(`Acompte 30% TTC = ${EUR(acompte)}`, totBoxX + totBoxW / 2, y + 6, { align: "center" });
  y += 14;

  // ============================================================
  // PORTES COULISSANTES (specs à commander séparément)
  // ============================================================
  if (b.sliding_doors_specs) {
    const s = b.sliding_doors_specs;
    if (y > H - 60) { doc.addPage(); y = 20; }
    doc.setFillColor(4, 101, 193);
    doc.roundedRect(MARGIN, y, INNER_W, 6, 2, 2, "F");
    doc.setTextColor(253, 251, 247);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Dimensions des portes coulissantes à commander (non fournies)", MARGIN + 3, y + 4.2);
    y += 9;

    doc.setDrawColor(4, 101, 193);
    doc.setLineWidth(0.4);
    doc.rect(MARGIN, y, INNER_W, 26);

    const cW = INNER_W / 3;
    const cx1 = MARGIN + cW / 2;
    const cx2 = MARGIN + cW * 1.5;
    const cx3 = MARGIN + cW * 2.5;
    doc.setTextColor(92, 88, 82);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("VANTAUX", cx1, y + 6, { align: "center" });
    doc.text("HAUTEUR", cx2, y + 6, { align: "center" });
    doc.text("LARGEUR UNITAIRE", cx3, y + 6, { align: "center" });
    doc.setTextColor(4, 101, 193);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(String(s.count), cx1, y + 16, { align: "center" });
    doc.text(`${s.vantail_height_mm} mm`, cx2, y + 16, { align: "center" });
    doc.text(`${s.vantail_width_mm} mm`, cx3, y + 16, { align: "center" });
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(140, 135, 128);
    doc.text("Recouvrement 20 mm", cx1, y + 22, { align: "center" });
    doc.text(`Passage rails ${s.rail_reserve_mm} mm`, cx2, y + 22, { align: "center" });
    doc.text("Fournisseur habituel", cx3, y + 22, { align: "center" });
    y += 30;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(92, 88, 82);
    const noteLines = doc.splitTextToSize(s.note || "", INNER_W);
    doc.text(noteLines, MARGIN, y);
    y += noteLines.length * 4 + 4;
  }

  // ============================================================
  // BOUTON CONVERTIR EN COMMANDE
  // ============================================================
  if (y > H - 30) { doc.addPage(); y = 20; }
  const convertUrl = opts.convertUrl || `${window.location.origin}/devis/${quote.id}?action=convert-to-order`;
  const btnW = 90;
  const btnH = 11;
  const btnX = (W - btnW) / 2;
  doc.setFillColor(4, 101, 193);
  doc.roundedRect(btnX, y, btnW, btnH, 5, 5, "F");
  doc.setTextColor(253, 251, 247);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.textWithLink("Valider ce devis", btnX + btnW / 2, y + btnH / 2 + 1, {
    align: "center",
    baseline: "middle",
    url: convertUrl,
  });
  y += btnH + 6;

  // ============================================================
  // FOOTER (marque blanche : mention neutre)
  // ============================================================
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(92, 88, 82);
  const footerY = Math.min(y, H - 15);
  doc.text("Devis valable 30 jours — Prix HT — Livraison de 2 à 4 semaines", W / 2, footerY, { align: "center" });

  if (quote.client && quote.client.notes) {
    const notesY = Math.min(footerY + 6, H - 8);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(45, 42, 38);
    const notes = doc.splitTextToSize(`Notes : ${quote.client.notes}`, INNER_W);
    doc.text(notes, MARGIN, notesY);
  }

  // Ouverture du PDF dans un nouvel onglet séparé (au lieu du téléchargement automatique) —
  // le PDF est présenté au professionnel dans un onglet dédié, qu'il peut ensuite Save-As s'il le souhaite.
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    // Fallback si les popups sont bloqués : téléchargement classique
    doc.save(`${quote.reference}.pdf`);
  } else {
    // Libère l'URL après ~1 min (le temps que l'onglet ait chargé)
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}
